// Load polyfills (Object.groupBy, Array.prototype.at, String.prototype.replaceAll) before the route tree
// evaluates, since router.tsx is the client entry and routeTree.gen pulls in every route module.
import './polyfills'
import type { ReactNode } from 'react'
import { isServer } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { routeTree } from './routeTree.gen'
import { makeQueryClient, queryClient as clientQueryClient, persistOptions, restorePersistedCache } from './main'
import { afterSsrPayloadApplied } from './lib/query-persistence'
import { useAppStore } from './lib/app-store'
import { createLocaleI18n, loadTranslation } from './lib/locales'
import { resolveServerLocale } from './lib/server-locale'
import { localePath, splitLocalePath } from './lib/locale-path'
import type { Locale } from 'shared/locales'
import { setupAnalytics } from './lib/analytics'
import { DefaultRouteError } from './route-error'
import { preloadModalSlot } from './components/custom/modal-slot'
import { preloadMatchedPanes } from './lib/lazy-panes'
import { logFrontendError } from './lib/log-error'
import { addBreadcrumb, installBreadcrumbCapture } from './lib/breadcrumbs'
import { getTraceId, startNavigationTrace } from './lib/client-trace'
import { installVersionCapture, suppressTanstackChunkReload } from './lib/app-version'
import { captureViewTransitionsSettled } from './lib/view-transition-capture'

/**
 * Router factory consumed by TanStack Start. The app is full-SSR: loaders run on the server and deferred
 * prefetches stream their result. That is load-bearing for the report routes — the server-rendered HTML is what
 * paints the report on reload (the persisted cache deliberately loses that render — docs/notes/app/query-persistence.md), so
 * they must NOT be
 * `ssr: 'data-only'`. The query client must be per-request on the server; a module singleton would leak one
 * request's dehydrated cache into the next on the long-lived server.
 */
export async function getRouter() {
  const queryClient = isServer ? makeQueryClient() : clientQueryClient

  // Resolve the request's locale ONCE, here, so SSR and the first client render agree — and load +
  // activate its catalog BEFORE the router is created and React renders/hydrates.
  const locale: Locale = isServer ? (resolveServerLocale() ?? 'es-ES') : useAppStore.getState().store.locale

  // Request-scoped Lingui instance: on the server this isolates each request's active locale from concurrent
  // renders sharing the worker (React #418 hydration mismatches). See createLocaleI18n.
  const i18nInstance = createLocaleI18n()
  await loadTranslation(locale, i18nInstance)

  // Per router instance, so one SSR request's prefix never leaks into another's output. `hasPrefix` is what keeps
  // the two halves symmetric: a visitor who arrived at a bare URL keeps bare URLs, one who arrived at `/en/...`
  // keeps the prefix on every link — so no navigation silently changes the language the URL claims.
  const localeState = { locale, hasPrefix: false }

  const router = createRouter({
    routeTree,
    /**
     * The locale segment is NOT in the route tree: it is stripped off the incoming URL and put back on every URL
     * the router emits, so `/en/privacy-policy` matches the same route as `/privacy-policy` and every `<Link>`
     * keeps the visitor's locale with no `to` anywhere in the app naming it. Only the public, indexable pages
     * carry it (lib/locale-path.ts).
     */
    rewrite: {
      input: ({ url }) => {
        const { locale: pathLocale, path } = splitLocalePath(url.pathname)
        localeState.hasPrefix = pathLocale !== undefined
        if (pathLocale) {
          localeState.locale = pathLocale
          url.pathname = path
        }
        return url
      },
      output: ({ url }) => {
        if (localeState.hasPrefix) url.pathname = localePath(url.pathname, localeState.locale)
        return url
      },
    },
    // Loaders prefetch into this client via `context.queryClient`; the ssr-query
    // integration below dehydrates the same instance and streams it to the client.
    context: {
      queryClient,
      locale,
      i18n: i18nInstance,
      // `router` is assigned by the time a loader calls this.
      preloadModal: (path: unknown) => preloadModalSlot(router, path),
    },
    defaultPreload: 'intent',
    // Loader calls must never be stale, so the loader runs whenever a route is preloaded or visited.
    defaultPreloadStaleTime: 0,
    /**
     * Every route with a component declares its own skeleton (scripts/check-route-pending.mjs), shown on the click
     * frame: the router awaits a route's chunk before rendering anything, and at the library defaults (1000/500) a
     * cold navigation left the previous page on screen for the whole download. No defaultPendingComponent, on
     * purpose: a component-less data layout must have NOTHING to present, or its search re-key would blank its
     * subtree at 0 (docs/notes/scripts/route-pending-check.md).
     */
    defaultPendingMs: 0,
    defaultPendingMinMs: 0,
    /**
     * A hover preload caches the destination as a `success` match the instant its loader resolves — every loader
     * here is fire-and-forget — while its chunk is still downloading. The click that follows inside that window
     * adopted it as a BACKGROUND reload of a stale match: no pending UI, the page you left frozen until the chunk
     * landed (measured: 222ms at a 300ms chunk, only on section roots WITH a loader — teams, employees, orgs).
     * Blocking makes a stale match pend, so the skeleton paints on the click frame; it costs nothing, since no
     * loader here ever awaits anything.
     */
    defaultStaleReloadMode: 'blocking',
    // A suspense query throws during render, so without this an auth/test error on a streamed query crashes the whole
    // tree. Pass the request-scoped i18n: the boundary renders ABOVE AppShell's provider and must not localize
    // against a concurrent request's locale (React #418).
    defaultErrorComponent: (props) => <DefaultRouteError {...props} i18n={i18nInstance} />,
    Wrap: (props: { children: ReactNode }) => {
      return (
        <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
          {props.children}
        </PersistQueryClientProvider>
      )
    },
  })

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
    wrapQueryClient: false,
  })

  // Google Analytics (client + prod only). Consent-gated: GA loads and tracks only after the visitor
  // opts in to statistics cookies, and reacts to later changes. See lib/analytics.ts + cookie-consent.ts.
  setupAnalytics(router)

  if (!isServer) {
    captureViewTransitionsSettled(router)
    // The persisted cache must lose the FIRST render to this document's payload, or React repaints text the
    // server already wrote (docs/notes/app/query-persistence.md). `setupRouterSsrQueryIntegration` has just installed the
    // hook that applies `query.initial`, and `load-client` AWAITS it before it builds a match or mounts
    // RouterProvider — so restoring from inside it is still pre-render, only ordered.
    const hydrateDocumentQueries = router.options.hydrate
    router.options.hydrate = async (dehydrated: Parameters<NonNullable<typeof hydrateDocumentQueries>>[0]) => {
      const detailPanes = preloadMatchedPanes(router, router.parseLocation(router.history.location).pathname)
      await hydrateDocumentQueries?.(dehydrated)
      restorePersistedCache()
      await detailPanes
    }

    // Only for a document with NO payload, where that hook never runs and there is nothing to lose to: on a
    // browser-restored document parsing is already finished, so registering this always would run the restore
    // SYNCHRONOUSLY ahead of the hook — the order this exists to stop.
    if (!window.$_TSR) afterSsrPayloadApplied(restorePersistedCache)

    // A dynamic import that cannot be fetched fires `vite:preloadError`. Do NOT reload: hashed assets persist on the
    // CDN, so a failed import is almost always the network, and reloading into a dead network blanks the page.
    // Suppress TanStack's own automatic reload too, so the rejection reaches the route error boundary.
    window.addEventListener('vite:preloadError', (event) => {
      suppressTanstackChunkReload((event as Event & { payload?: unknown }).payload)
    })

    // A new build deployed mid-session keeps the client on stale code. The next navigation goes through the document
    // (the root beforeLoad, lib/app-version) — or a reload, if it crashes first on a payload the new build reshaped
    // (#88, handled in route-error). This just records the version every response advertises.
    installVersionCapture()

    // Breadcrumbs are in-memory and cost nothing until a crash; the session timeline POSTs batches, so it is only
    // fetched, as its own chunk, when the wide-events sink is enabled. Calls queue on the import, in order.
    // A chunk that fails to load (a deploy mid-session) costs the timeline, never an unhandled rejection.
    const timeline =
      window.WIDE_EVENTS_ENABLED === 'true' ? import('./lib/session-actions').catch(() => undefined) : undefined
    // Read now, not when the chunk lands: a user who clicks on before it loads must still be recorded landing here.
    const landing = { path: window.location.pathname, trace: getTraceId(), t: Date.now() }
    // The route TEMPLATE (`/jobs/$jobId`), so the timeline groups by page whatever the ids.
    const currentRoute = () => router.state.matches.at(-1)?.fullPath
    // Before the new page's loaders run, so their calls carry its trace; a filter or a tab within a page keeps it.
    router.subscribe('onBeforeNavigate', (event) => {
      if (!event.fromLocation || !event.pathChanged) return
      startNavigationTrace()
      const from = event.fromLocation.pathname
      const fromRoute = currentRoute()
      const trace = getTraceId()
      const at = performance.now()
      void timeline?.then((m) => m?.beginNavigation(from, trace, at, fromRoute))
    })
    router.subscribe('onResolved', (event) => {
      addBreadcrumb('nav', event.toLocation.href)
      const from = event.fromLocation?.href
      void timeline?.then((m) => m?.trackNavigation(event.toLocation.href, from))
    })
    installBreadcrumbCapture()
    void timeline?.then((m) => m?.installClientActionCapture({ currentRoute, landing }))

    // Errors that escape React's error boundaries — async callbacks, event handlers, setTimeout, etc.
    // — never reach `defaultErrorComponent`, so report them here too.
    window.addEventListener('error', (event) => {
      // Cross-origin script errors (GA, third-party embeds) surface with `event.error == null` and no
      // actionable detail — skip them.
      if (event.error == null) return
      logFrontendError(event.error, 'global')
    })

    window.addEventListener('unhandledrejection', (event) => {
      logFrontendError(event.reason, 'unhandledrejection')
    })
  }

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: Awaited<ReturnType<typeof getRouter>>
  }
}
